/**
 * @file paymentApi.ts
 * @description RTK Query API slice for USDT/BSC payment operations.
 */
import { createApi } from '@reduxjs/toolkit/query/react';
import type { UserPlan, BillingInterval } from '@forex-builder/shared-types';
import { createBaseQueryWithReauth } from '../../../shared/api/baseQueryWithReauth.js';
import type { RootState } from '../../../app/store.js';
import { updateUser } from '../../auth/model/authSlice.js';
import { subscriptionApi } from './subscriptionApi.js';

// ── Response Types ────────────────────────────────────────────────────────────

export interface DepositInfoResponse {
  success: boolean;
  data: {
    address: string;
    balance: number;
    onChainBalance: number;
  };
}

export interface BalanceResponse {
  success: boolean;
  data: {
    balance: number;
  };
}

export interface CheckDepositResponse {
  success: boolean;
  message: string;
  data: {
    credited: number;
    balance: number;
  };
}

export interface PurchasePlanResponse {
  success: boolean;
  message: string;
  data: {
    plan: string;
    balance: number;
  };
}

export interface Transaction {
  _id: string;
  userId: string;
  type: 'deposit' | 'plan_purchase' | 'withdrawal';
  amount: number;
  planId?: string;
  txHash?: string;
  toAddress?: string;
  status: 'pending' | 'confirmed' | 'failed';
  createdAt: string;
  updatedAt: string;
}

export interface TransactionsResponse {
  success: boolean;
  data: {
    transactions: Transaction[];
  };
}

// ── API Slice ─────────────────────────────────────────────────────────────────

export const paymentApi = createApi({
  reducerPath: 'paymentApi',
  baseQuery: createBaseQueryWithReauth('/api/v1/payment'),
  tagTypes: ['Balance', 'DepositInfo', 'Transactions'],
  endpoints: (builder) => ({
    getDepositAddress: builder.query<DepositInfoResponse, void>({
      query: () => '/deposit-address',
      providesTags: ['DepositInfo'],
    }),

    getBalance: builder.query<BalanceResponse, void>({
      query: () => '/balance',
      providesTags: ['Balance'],
    }),

    checkDeposit: builder.mutation<CheckDepositResponse, void>({
      query: () => ({
        url: '/check-deposit',
        method: 'POST',
      }),
      invalidatesTags: ['Balance', 'DepositInfo', 'Transactions'],
    }),

    purchasePlan: builder.mutation<
      PurchasePlanResponse,
      { planId: UserPlan; interval?: BillingInterval }
    >({
      query: (body) => ({
        url: '/purchase-plan',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Balance', 'Transactions'],
      async onQueryStarted({ planId }, { dispatch, queryFulfilled, getState }) {
        try {
          await queryFulfilled;
          // Invalidate subscription summary to refresh limits, entitlements & plan status immediately
          dispatch(subscriptionApi.util.invalidateTags(['Subscription']));

          // Update user in auth slice and localStorage
          const state = getState() as RootState;
          if (state.auth?.user) {
            dispatch(updateUser({ ...state.auth.user, plan: planId }));
          }
        } catch {
          // Request failed; do nothing
        }
      },
    }),

    getTransactions: builder.query<TransactionsResponse, void>({
      query: () => '/transactions',
      providesTags: ['Transactions'],
    }),
  }),
});

export const {
  useGetDepositAddressQuery,
  useGetBalanceQuery,
  useCheckDepositMutation,
  usePurchasePlanMutation,
  useGetTransactionsQuery,
} = paymentApi;
